import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { Link } from '@/utils/router';
import Footer from '@/components/Footer/Footer';
import MenuDesktop from '@/components/MenuDesktop/MenuDesktop';
import MenuMobile from '@/components/MenuMobile/MenuMobile';
import {
  HEADER_HEIGHT,
  MEDIA_BREAKPOINTS,
  SEPARATOR_HEIGHT,
} from '@/constants/layout';
import { getPlanningGuideGroups } from '@/constants/event-planning';
import {
  globalTypePath,
  typeLocationPath,
  type EventType,
  type Region,
} from '@/constants/geo';
import { useI18n } from '@/i18n/LanguageProvider';
import { useTheme } from '@/theme/ThemeProvider';
import type { ThemeMode } from '@/theme/themes';
import {
  getPlanningPageBackground,
  getPlanningPageTheme,
  getPlanningScrollbarColor,
  getPlanningScrollbarThumb,
  getPlanningScrollbarThumbHover,
  getPlanningScrollbarTrack,
  type PlanningThemeVariant,
} from '@/pages/planningTheme';
import {
  SEO_SERVICE_BAR_BAT,
  SEO_SERVICE_EVENT,
  SEO_SERVICE_HENNA,
  SEO_SERVICE_WEDDING,
  withBet,
  type SeoService,
} from '@/constants/core';
import { ICONS } from '@/constants/icons';
import { scheduleSpaReadyAfterPaint } from '@/utils/spaReady';

type PageDirection = 'rtl' | 'ltr';
type PlanningGuideGroup = ReturnType<typeof getPlanningGuideGroups>[number];
type PlanningGuideItem = PlanningGuideGroup['items'][number];
type PlanningGuideParentLink = { href: string; label: string };
type PlanningGuideIntroHighlight = { title: string; text: string };
type PlanningGuideIntro = {
  menuLabel: string;
  title: string;
  lead: string;
  paragraphs: string[];
  highlights: PlanningGuideIntroHighlight[];
  serviceCtaTitle: string;
  serviceCtaText: string;
};
type PlanningGuideSectionId =
  | 'planning-guide-overview'
  | 'planning-guide-wedding'
  | 'planning-guide-henna'
  | 'planning-guide-bar-bat'
  | 'planning-guide-event'
  | 'planning-guide-venues';

const PLANNING_GUIDE_OVERVIEW_SECTION_ID = 'planning-guide-overview';
const PLANNING_GUIDE_VENUES_SECTION_ID = 'planning-guide-venues';
const PLANNING_GUIDE_SERVICE_SECTION_IDS: Partial<
  Record<SeoService, PlanningGuideSectionId>
> = {
  [SEO_SERVICE_WEDDING]: 'planning-guide-wedding',
  [SEO_SERVICE_HENNA]: 'planning-guide-henna',
  [SEO_SERVICE_BAR_BAT]: 'planning-guide-bar-bat',
  [SEO_SERVICE_EVENT]: 'planning-guide-event',
};
const mediumScreenBreakpoint = parseInt(MEDIA_BREAKPOINTS.mediumScreen, 10);

const isCityVenueGuideGroup = (group: PlanningGuideGroup) =>
  String(group.service).startsWith('city_venue_guides_');

const getPlanningGuideGroupSectionId = (
  group: PlanningGuideGroup,
): PlanningGuideSectionId | undefined => {
  if (isCityVenueGuideGroup(group)) return PLANNING_GUIDE_VENUES_SECTION_ID;

  return PLANNING_GUIDE_SERVICE_SECTION_IDS[group.service as SeoService];
};

const getCompactPlanningMenuLabel = (title: string) => {
  const compactTitle = title
    .trim()
    .replace(/^ארגון\s+/, '')
    .replace(/\s+Planning$/i, '')
    .replace(/^Organisation\s+(?:de\s+|d’|d')?/i, '')
    .replace(/^Планирование\s+/i, '');

  return compactTitle || title;
};

const PLANNING_GUIDE_MENU_LABELS: Record<
  string,
  Partial<Record<PlanningGuideSectionId, string>>
> = {
  he: {
    'planning-guide-overview': 'מדריך אירועים',
    'planning-guide-wedding': 'חתונה',
    'planning-guide-henna': 'חינה',
    'planning-guide-bar-bat': 'בר/בת',
    'planning-guide-event': 'אירוע',
    'planning-guide-venues': 'אולמות',
  },
  en: {
    'planning-guide-overview': 'Overview',
    'planning-guide-wedding': 'Wedding',
    'planning-guide-henna': 'Henna',
    'planning-guide-bar-bat': 'Bar/Bat',
    'planning-guide-event': 'Event',
    'planning-guide-venues': 'Venues',
  },
  fr: {
    'planning-guide-overview': 'Aperçu',
    'planning-guide-wedding': 'Mariage',
    'planning-guide-henna': 'Henné',
    'planning-guide-bar-bat': 'Bar/Bat',
    'planning-guide-event': 'Événement',
    'planning-guide-venues': 'Salles',
  },
  ru: {
    'planning-guide-overview': 'Обзор',
    'planning-guide-wedding': 'Свадьба',
    'planning-guide-henna': 'Хина',
    'planning-guide-bar-bat': 'Бар/Бат',
    'planning-guide-event': 'Событие',
    'planning-guide-venues': 'Площадки',
  },
};

const getPlanningGuideMenuLabel = (
  lang: string,
  sectionId: PlanningGuideSectionId,
  fallback: string,
) =>
  PLANNING_GUIDE_MENU_LABELS[lang]?.[sectionId] ??
  getCompactPlanningMenuLabel(fallback);

const getGuideCarouselVisibleCount = () => {
  if (typeof window === 'undefined') return 3;

  const isMobile = window.matchMedia(
    `(max-width: ${MEDIA_BREAKPOINTS.mediumScreen})`,
  ).matches;
  if (isMobile) return 1;

  const isTablet = window.matchMedia('(max-width: 980px)').matches;
  return isTablet ? 2 : 3;
};

const PlanningGlobalStyle = createGlobalStyle<{
  $mode: ThemeMode;
  $variant?: PlanningThemeVariant;
}>`
  html {
    scrollbar-color: ${({ $mode, $variant }) =>
      getPlanningScrollbarColor($mode, $variant)};
  }

  body,
  #root {
    background: ${({ $mode, $variant }) =>
      getPlanningPageBackground($mode, $variant)};
  }

  ::-webkit-scrollbar-track {
    background: ${({ $mode, $variant }) =>
      getPlanningScrollbarTrack($mode, $variant)};
  }

  ::-webkit-scrollbar-thumb {
    background: ${({ $mode, $variant }) =>
      getPlanningScrollbarThumb($mode, $variant)};
    border: 3px solid
      ${({ $mode, $variant }) => getPlanningScrollbarTrack($mode, $variant)};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${({ $mode, $variant }) =>
      getPlanningScrollbarThumbHover($mode, $variant)};
  }
`;

const Page = styled.div<{
  $dir: PageDirection;
  $mode: ThemeMode;
  $variant?: PlanningThemeVariant;
}>`
  min-height: 100vh;
  overflow-x: hidden;
  direction: ${({ $dir }) => $dir};
  color: var(--planning-ink);
  font-family: ${({ theme }) => theme.fonts.rubikBoldItalic};
  transition:
    background 0.25s ease,
    color 0.25s ease;

  ${({ $mode, $variant }) => getPlanningPageTheme($mode, $variant)}

  background: var(--planning-page-bg);
`;

const Main = styled.main`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  max-width: 1120px;
  margin: 0 auto;
  padding: ${HEADER_HEIGHT + 2}px 22px 78px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 10px 14px 52px;
  }
`;

const Overview = styled.section`
  min-width: 0;
  padding: clamp(4px, 1.2vw, 12px) 0 clamp(34px, 5vw, 62px);
  border-bottom: 1px solid var(--planning-border-muted);

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding-top: 2px;
  }
`;

const Hero = styled.div`
  min-width: 0;
  text-align: center;
  max-width: 860px;
  margin: 0 auto clamp(14px, 2vw, 24px);

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    margin-bottom: 26px;
  }

  h1 {
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: clamp(2.35rem, 5.8vw, 4.45rem);
    line-height: 1.05;
    color: var(--planning-accent-soft);
    text-shadow: 0 12px 36px var(--planning-shadow-strong);
  }

  p {
    margin: 16px auto 0;
    color: var(--planning-muted);
    font-size: clamp(1rem, 1.8vw, 1.18rem);
    line-height: 1.75;
  }
`;

const OverviewLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(280px, 0.72fr);
  gap: clamp(18px, 4vw, 34px);
  align-items: stretch;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
  }
`;

const OverviewCopy = styled.div`
  min-width: 0;
  display: grid;
  align-content: center;
  gap: 16px;
  padding-inline-end: clamp(0px, 2vw, 18px);

  h2 {
    margin: 0;
    color: var(--planning-heading);
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: clamp(1.55rem, 3.2vw, 2.35rem);
    line-height: 1.12;
  }

  p {
    margin: 0;
    color: var(--planning-muted);
    font-size: clamp(1rem, 1.5vw, 1.1rem);
    line-height: 1.78;
  }
`;

const IntroLead = styled.p`
  color: var(--planning-heading) !important;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
`;

const OverviewHighlights = styled.ul`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 0;
  margin: 8px 0 0;
  list-style: none;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
  }
`;

const OverviewHighlight = styled.li`
  min-width: 0;
  border-top: 1px solid var(--planning-border);
  padding-top: 12px;

  strong {
    display: block;
    color: var(--planning-accent-soft);
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    line-height: 1.25;
  }

  span {
    display: block;
    margin-top: 6px;
    color: var(--planning-muted);
    line-height: 1.55;
    font-size: 0.94rem;
  }
`;

const ServiceCta = styled.nav`
  min-width: 0;
  align-self: stretch;
  display: grid;
  align-content: center;
  gap: 12px;
  padding: clamp(18px, 3vw, 24px);
  border: 1px solid var(--planning-border);
  border-radius: 8px;
  background: var(--planning-panel);
  backdrop-filter: blur(12px);
  box-shadow: 0 22px 48px var(--planning-shadow);

  h3 {
    margin: 0;
    color: var(--planning-heading);
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: clamp(1.05rem, 2vw, 1.28rem);
    line-height: 1.2;
    text-align: center;
  }

  p {
    margin: 0;
    color: var(--planning-muted);
    font-size: 0.98rem;
    line-height: 1.62;
    text-align: center;
  }
`;

const ServiceCtaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 9px;
`;

const ServiceCtaLink = styled(Link)`
  display: inline-flex;
  min-width: 0;
  min-height: 42px;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--planning-border);
  color: var(--planning-menu-ink);
  background: var(--planning-button-bg);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: 0.95rem;
  line-height: 1.25;
  text-align: center;
  text-decoration: none;
  overflow-wrap: anywhere;

  &:hover,
  &:focus-visible {
    color: var(--planning-button-hover-ink);
    background: var(--planning-button-hover-bg);
    border-color: var(--planning-border-strong);
    outline: 2px solid var(--planning-border-strong);
    outline-offset: 3px;
  }
`;

const ServiceContactRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
`;

const ServiceContactLink = styled.a<{ $tone?: 'solid' | 'soft' }>`
  display: inline-flex;
  min-width: 0;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--planning-border-strong);
  color: ${({ $tone }) =>
    $tone === 'solid'
      ? 'var(--planning-button-hover-ink)'
      : 'var(--planning-menu-ink)'};
  background: ${({ $tone }) =>
    $tone === 'solid'
      ? 'var(--planning-button-hover-bg)'
      : 'var(--planning-button-bg)'};
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: 0.95rem;
  line-height: 1.25;
  text-align: center;
  text-decoration: none;
  overflow-wrap: anywhere;

  svg {
    width: 1em;
    height: 1em;
    flex: 0 0 auto;
  }

  &:hover,
  &:focus-visible {
    color: var(--planning-button-hover-ink);
    background: var(--planning-button-hover-bg);
    border-color: var(--planning-border-strong);
    outline: 2px solid var(--planning-border-strong);
    outline-offset: 3px;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    min-height: 42px;
  }
`;

const Groups = styled.div`
  display: grid;
  min-width: 0;
  gap: 22px;
`;

const GroupHeader = styled.div`
  display: flex;
  min-width: 0;
  max-width: 100%;
  align-items: baseline;
  justify-content: flex-start;
  gap: 8px 14px;
  flex-wrap: wrap;
  margin: 0 0 4px;

  h2 {
    min-width: 0;
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    font-size: clamp(1.45rem, 2.5vw, 2rem);
    color: var(--planning-accent-soft);
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    gap: 6px 12px;
  }
`;

const GroupParentLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-height: 32px;
  max-width: 100%;
  padding: 2px 0 3px;
  color: var(--planning-accent-soft);
  text-align: center;
  text-decoration: underline;
  text-decoration-color: var(--planning-accent);
  text-decoration-thickness: 1px;
  text-underline-offset: 5px;
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: clamp(0.9rem, 1.35vw, 1rem);
  line-height: 1.35;
  background: transparent;
  border: 0;
  box-shadow: none;
  white-space: nowrap;

  &:hover,
  &:focus-visible {
    color: var(--planning-heading);
    text-decoration-color: var(--planning-heading);
    outline: 2px solid var(--planning-border);
    outline-offset: 3px;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    min-height: 30px;
  }
`;

const Group = styled.section<{
  $mode: ThemeMode;
  $variant?: PlanningThemeVariant;
}>`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: clamp(20px, 3vw, 30px) 0;
  border-top: 1px solid var(--planning-border-muted);

  ${({ $mode, $variant }) =>
    $variant ? getPlanningPageTheme($mode, $variant) : ''}

  > p {
    margin: 0 0 12px;
    color: var(--planning-muted);
    line-height: 1.62;
  }
`;

const CardsCarousel = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 18px 58px;
  border: 1px solid var(--planning-border);
  border-radius: 8px;
  background: var(--planning-panel);
  backdrop-filter: blur(10px);
  overflow: hidden;
  isolation: isolate;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    padding: 14px 52px;
  }
`;

const CarouselControl = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({ $side }) => $side}: 4px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  box-sizing: border-box;
  transform: translateY(-50%);
  border: 1px solid var(--planning-border-strong);
  border-radius: 999px;
  background: var(--planning-panel-strong);
  color: var(--planning-accent-soft);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: 1.35rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 12px 24px var(--planning-shadow);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    background: var(--planning-card-hover);
    border-color: var(--planning-border-strong);
    color: var(--planning-heading);
    outline: 2px solid var(--planning-border-strong);
    outline-offset: 3px;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    ${({ $side }) => $side}: 5px;
    width: 40px;
    height: 40px;
  }
`;

const Cards = styled.div<{ $visible: number; $dir: PageDirection }>`
  direction: ${({ $dir }) => $dir};
  display: grid;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  grid-template-columns: ${({ $visible }) =>
    `repeat(${$visible}, minmax(0, 1fr))`};
  align-items: stretch;
  gap: 14px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  display: grid;
  grid-template-rows: auto auto 1fr;
  gap: 7px;
  width: 100%;
  min-width: 0;
  height: 390px;
  min-height: 390px;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: 8px;
  padding: 12px;
  color: var(--planning-ink);
  text-decoration: none;
  background: var(--planning-card);
  border: 1px solid var(--planning-border);
  backdrop-filter: blur(10px);
  box-shadow: 0 16px 34px rgba(8, 7, 13, 0.16);

  &:hover,
  &:focus-visible {
    background: var(--planning-card-hover);
    border-color: var(--planning-border-strong);
    outline: 2px solid var(--planning-border);
    outline-offset: 3px;
  }

  > img {
    width: 100%;
    box-sizing: border-box;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid var(--planning-border-muted);
    box-shadow: 0 8px 18px var(--planning-shadow);
  }

  > strong {
    font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
    color: var(--planning-accent-soft);
    line-height: 1.28;
    display: -webkit-box;
    overflow: hidden;
    overflow-wrap: anywhere;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  > span {
    color: var(--planning-muted);
    line-height: 1.48;
    font-size: 0.94rem;
    display: -webkit-box;
    max-height: calc(1.48em * 6);
    overflow: hidden;
    text-overflow: ellipsis;
    overflow-wrap: anywhere;
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;
  }

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    grid-template-rows: auto auto auto;
    height: auto;
    min-height: 0;
    gap: 8px;

    > span {
      max-height: calc(1.48em * 6);
      -webkit-line-clamp: 6;
    }
  }
`;

const CityVenueVisual = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  box-sizing: border-box;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  border-radius: 6px;
  border: 1px solid rgba(216, 199, 255, 0.26);
  background:
    radial-gradient(
      circle at 50% -12%,
      var(--planning-visual-glow) 0%,
      var(--planning-visual-glow-soft) 30%,
      transparent 58%
    ),
    linear-gradient(
      90deg,
      transparent 0%,
      var(--planning-visual-glow-soft) 18%,
      transparent 34%,
      transparent 66%,
      var(--planning-visual-glow-soft) 82%,
      transparent 100%
    ),
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.08) 0%,
      transparent 34%,
      var(--planning-shadow) 100%
    ),
    linear-gradient(
      135deg,
      var(--planning-visual-bg-start) 0%,
      var(--planning-visual-bg-mid) 52%,
      var(--planning-visual-bg-end) 100%
    );
  box-shadow: 0 8px 18px var(--planning-shadow);
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 13px;
    border: 1px solid var(--planning-border-muted);
    border-radius: 6px;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset-inline: 18px;
    bottom: 20px;
    height: 2px;
    background: linear-gradient(
      90deg,
      transparent,
      var(--planning-accent),
      transparent
    );
    opacity: 0.78;
  }
`;

const CityVenueContent = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  gap: 7px;
  width: min(86%, 320px);
  padding: 18px 16px 22px;
  text-align: center;
`;

const CityVenueKicker = styled.span`
  color: var(--planning-accent-soft);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: clamp(0.82rem, 1.2vw, 0.96rem);
  line-height: 1.2;
`;

const CityVenueName = styled.span`
  max-width: 100%;
  color: var(--planning-heading);
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: clamp(1.45rem, 2.35vw, 2.05rem);
  line-height: 1.05;
  overflow-wrap: anywhere;
  text-shadow: 0 2px 16px var(--planning-shadow-strong);
`;

const CityVenueMark = styled.div`
  position: absolute;
  inset-inline: 26px;
  bottom: 18px;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  justify-content: center;
  gap: 5px;
  opacity: 0.34;
  z-index: 0;

  span {
    height: 30px;
    border: 1px solid var(--planning-border);
    border-radius: 4px 4px 0 0;
    background: var(--planning-button-bg);
  }

  span:nth-child(2) {
    height: 44px;
  }

  span:nth-child(3) {
    height: 24px;
  }

  span:nth-child(4) {
    height: 52px;
  }
`;

function GuideCardMedia({
  cityLabel,
  cityVisualLabel,
  item,
}: {
  cityLabel?: string;
  cityVisualLabel: string;
  item: PlanningGuideItem;
}) {
  if (cityLabel) {
    return (
      <CityVenueVisual aria-hidden="true">
        <CityVenueMark>
          <span />
          <span />
          <span />
          <span />
          <span />
        </CityVenueMark>
        <CityVenueContent>
          <CityVenueKicker>{cityVisualLabel}</CityVenueKicker>
          <CityVenueName>{cityLabel}</CityVenueName>
        </CityVenueContent>
      </CityVenueVisual>
    );
  }

  return (
    <img src={item.image} alt={item.imageAlt} loading="lazy" decoding="async" />
  );
}

export default function PlanningGuidePage() {
  const { i18n, lang } = useI18n();
  const { mode } = useTheme();
  const direction: PageDirection = lang === 'he' ? 'rtl' : 'ltr';
  const groups = useMemo(() => getPlanningGuideGroups(i18n), [i18n]);
  const guideIntro = i18n.PLANNING_GUIDE_INTRO as PlanningGuideIntro;
  const [carouselIndexes, setCarouselIndexes] = useState<
    Record<string, number>
  >({});
  const [visibleCarouselCount, setVisibleCarouselCount] = useState(
    getGuideCarouselVisibleCount,
  );
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedMenuSection, setSelectedMenuSection] =
    useState<PlanningGuideSectionId>(PLANNING_GUIDE_OVERVIEW_SECTION_ID);
  const selectedMenuSectionRef = useRef<PlanningGuideSectionId>(
    PLANNING_GUIDE_OVERVIEW_SECTION_ID,
  );
  const scrollTargetsRef = useRef<Map<PlanningGuideSectionId, HTMLElement>>(
    new Map(),
  );
  const observedSectionsRef = useRef<
    Map<
      string,
      {
        sectionId: PlanningGuideSectionId;
        element: HTMLElement;
      }
    >
  >(new Map());
  const sectionBoundsRef = useRef<
    {
      sectionId: PlanningGuideSectionId;
      top: number;
      bottom: number;
    }[]
  >([]);
  const parentLinks: Array<{ service: SeoService; type: EventType }> = [
    { service: SEO_SERVICE_WEDDING, type: 'חתונה' },
    { service: SEO_SERVICE_HENNA, type: 'חינה' },
    { service: SEO_SERVICE_BAR_BAT, type: 'בר-מצווה' },
    { service: SEO_SERVICE_EVENT, type: 'אירוע' },
  ];
  const parentLinkByService = new Map(
    parentLinks.map(item => [
      item.service,
      {
        href: globalTypePath(item.type),
        label: i18n.preferredKeywordForService(item.service),
      },
    ]),
  );
  const serviceCtaLinks = parentLinks.map(item => ({
    href: globalTypePath(item.type),
    label: i18n.preferredKeywordForService(item.service),
  }));
  const serviceCtaPhoneHref = i18n.SECTION_FOUR_CONTACT_BUTTONS.phone.href;
  const serviceCtaWhatsappHref = i18n.buildWhatsAppWithText(
    i18n.PLANNING_WHATSAPP_TEXT(i18n.PLANNING_GUIDE_TITLE),
  );
  const PhoneIcon = ICONS.phone;
  const WhatsappIcon = ICONS.whatsapp;
  const buildRegionParentLink = (
    region?: Region,
  ): PlanningGuideParentLink | undefined => {
    if (!region) return undefined;

    const label = i18n.preferredKeywordForService(SEO_SERVICE_EVENT);

    return {
      href: typeLocationPath('אירוע', region),
      label:
        i18n.HTML_LANG === 'he'
          ? `${label}${withBet(region)}`
          : `${label} - ${i18n.translateRegion(region)}`,
    };
  };
  const firstVenueGroupIndex = useMemo(
    () => groups.findIndex(isCityVenueGuideGroup),
    [groups],
  );
  const guideMenuSections = useMemo(() => {
    const sections: { id: PlanningGuideSectionId; label: string }[] = [
      {
        id: PLANNING_GUIDE_OVERVIEW_SECTION_ID,
        label: getPlanningGuideMenuLabel(
          lang,
          PLANNING_GUIDE_OVERVIEW_SECTION_ID,
          guideIntro.menuLabel,
        ),
      },
    ];
    let didAddVenueGuides = false;

    groups.forEach(group => {
      const sectionId = getPlanningGuideGroupSectionId(group);
      if (!sectionId) return;

      if (sectionId === PLANNING_GUIDE_VENUES_SECTION_ID) {
        if (didAddVenueGuides) return;

        didAddVenueGuides = true;
        sections.push({
          id: sectionId,
          label: getPlanningGuideMenuLabel(
            lang,
            sectionId,
            i18n.PLANNING_CITY_VENUE_GUIDE.visualLabel,
          ),
        });
        return;
      }

      sections.push({
        id: sectionId,
        label: getPlanningGuideMenuLabel(lang, sectionId, group.title),
      });
    });

    return sections;
  }, [
    groups,
    lang,
    i18n.PLANNING_CITY_VENUE_GUIDE.visualLabel,
    guideIntro.menuLabel,
  ]);

  const togglePlanningMenu = useCallback(() => {
    setIsMenuOpen(prev => !prev);
  }, []);

  const registerPlanningSection = useCallback(
    (key: string, sectionId: PlanningGuideSectionId, isScrollTarget = true) =>
      (element: HTMLElement | null) => {
        const previous = observedSectionsRef.current.get(key);

        if (!element) {
          if (
            previous &&
            scrollTargetsRef.current.get(previous.sectionId) ===
              previous.element
          ) {
            scrollTargetsRef.current.delete(previous.sectionId);
          }

          observedSectionsRef.current.delete(key);
          return;
        }

        if (
          previous &&
          previous.element !== element &&
          scrollTargetsRef.current.get(previous.sectionId) === previous.element
        ) {
          scrollTargetsRef.current.delete(previous.sectionId);
        }

        observedSectionsRef.current.set(key, { sectionId, element });

        if (isScrollTarget) {
          scrollTargetsRef.current.set(sectionId, element);
        }
      },
    [],
  );

  const scrollToPlanningSection = useCallback(
    (sectionId: PlanningGuideSectionId) => {
      const element =
        scrollTargetsRef.current.get(sectionId) ??
        document.getElementById(sectionId);
      if (!element) return;

      scrollTargetsRef.current.set(sectionId, element);

      const top = element.getBoundingClientRect().top + window.pageYOffset;
      const isDesktopNow = window.innerWidth >= mediumScreenBreakpoint;
      const offset = isDesktopNow
        ? HEADER_HEIGHT + SEPARATOR_HEIGHT.largeScreen
        : SEPARATOR_HEIGHT.smallScreen;

      window.scrollTo({
        top: Math.max(0, top - offset),
        behavior: 'smooth',
      });

      selectedMenuSectionRef.current = sectionId;
      setSelectedMenuSection(sectionId);
    },
    [],
  );

  useEffect(() => {
    return scheduleSpaReadyAfterPaint();
  }, []);

  useEffect(() => {
    selectedMenuSectionRef.current = selectedMenuSection;
  }, [selectedMenuSection]);

  useEffect(() => {
    if (
      !guideMenuSections.some(section => section.id === selectedMenuSection)
    ) {
      selectedMenuSectionRef.current = PLANNING_GUIDE_OVERVIEW_SECTION_ID;
      setSelectedMenuSection(PLANNING_GUIDE_OVERVIEW_SECTION_ID);
    }
  }, [guideMenuSections, selectedMenuSection]);

  useEffect(() => {
    const updateVisibleCount = () => {
      setVisibleCarouselCount(getGuideCarouselVisibleCount());
    };

    updateVisibleCount();
    window.addEventListener('resize', updateVisibleCount);
    return () => window.removeEventListener('resize', updateVisibleCount);
  }, []);

  useEffect(() => {
    setCarouselIndexes(current => {
      let didChange = false;
      const next = { ...current };
      const groupKeys = new Set(groups.map(group => String(group.service)));

      Object.keys(next).forEach(groupKey => {
        if (!groupKeys.has(groupKey)) {
          delete next[groupKey];
          didChange = true;
        }
      });

      groups.forEach(group => {
        const groupKey = String(group.service);
        const visibleCount = Math.max(
          1,
          Math.min(group.items.length, visibleCarouselCount),
        );
        const maxIndex = Math.max(0, group.items.length - visibleCount);
        const currentIndex = next[groupKey] ?? 0;
        const clampedIndex = Math.max(0, Math.min(currentIndex, maxIndex));

        if (currentIndex !== clampedIndex) {
          next[groupKey] = clampedIndex;
          didChange = true;
        }
      });

      return didChange ? next : current;
    });
  }, [groups, visibleCarouselCount]);

  useEffect(() => {
    let raf: number | null = null;
    let measureRaf: number | null = null;

    const measureSections = () => {
      measureRaf = null;
      sectionBoundsRef.current = Array.from(
        observedSectionsRef.current.values(),
      )
        .map(({ sectionId, element }) => {
          const rect = element.getBoundingClientRect();
          const top = rect.top + window.scrollY;

          return {
            sectionId,
            top,
            bottom: top + rect.height,
          };
        })
        .sort((a, b) => a.top - b.top);
    };

    const scheduleMeasure = () => {
      if (measureRaf != null) return;
      measureRaf = window.requestAnimationFrame(measureSections);
    };

    const updateSelectedSection = () => {
      raf = null;

      if (!sectionBoundsRef.current.length) {
        measureSections();
      }

      const probeY = window.scrollY + window.innerHeight / 2;
      let current = selectedMenuSectionRef.current;

      for (const section of sectionBoundsRef.current) {
        if (probeY >= section.top && probeY < section.bottom) {
          current = section.sectionId;
          break;
        }
      }

      if (current !== selectedMenuSectionRef.current) {
        selectedMenuSectionRef.current = current;
        setSelectedMenuSection(current);
      }
    };

    const scheduleSelectedUpdate = () => {
      if (raf != null) return;
      raf = window.requestAnimationFrame(updateSelectedSection);
    };

    const handleScroll = () => {
      scheduleSelectedUpdate();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', scheduleMeasure, { passive: true });
    window.addEventListener('orientationchange', scheduleMeasure);

    scheduleMeasure();
    scheduleSelectedUpdate();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', scheduleMeasure);
      window.removeEventListener('orientationchange', scheduleMeasure);

      if (raf != null) {
        window.cancelAnimationFrame(raf);
      }
      if (measureRaf != null) {
        window.cancelAnimationFrame(measureRaf);
      }
    };
  }, [guideMenuSections.length, groups.length]);

  const moveCarousel = (
    groupKey: string,
    maxIndex: number,
    nextIndex: number,
  ) => {
    setCarouselIndexes(current => ({
      ...current,
      [groupKey]: Math.max(0, Math.min(maxIndex, nextIndex)),
    }));
  };

  const nextControlSide = direction === 'rtl' ? 'left' : 'right';
  const previousControlSide = direction === 'rtl' ? 'right' : 'left';
  const nextControlSymbol = direction === 'rtl' ? '←' : '→';
  const previousControlSymbol = direction === 'rtl' ? '→' : '←';
  const planningThemeVariant: PlanningThemeVariant = 'wedding';

  return (
    <>
      <PlanningGlobalStyle $mode={mode} $variant={planningThemeVariant} />
      <Page $dir={direction} $mode={mode} $variant={planningThemeVariant}>
        <MenuDesktop
          sections={guideMenuSections}
          selectedSection={selectedMenuSection}
          setSelectedSection={setSelectedMenuSection}
          scrollToSection={scrollToPlanningSection}
          variant="planning"
        />
        <MenuMobile
          sections={guideMenuSections}
          isMenuOpen={isMenuOpen}
          toggleMenu={togglePlanningMenu}
          selectedSection={selectedMenuSection}
          scrollToSection={scrollToPlanningSection}
          variant="planning"
        />

        <Main>
          <Overview
            id={PLANNING_GUIDE_OVERVIEW_SECTION_ID}
            ref={registerPlanningSection(
              'planning-guide-overview',
              PLANNING_GUIDE_OVERVIEW_SECTION_ID,
            )}
            aria-labelledby="planning-guide-title"
          >
            <Hero>
              <h1 id="planning-guide-title">{i18n.PLANNING_GUIDE_TITLE}</h1>
              <p>{i18n.PLANNING_GUIDE_SUBTITLE}</p>
            </Hero>

            <OverviewLayout>
              <OverviewCopy>
                <h2>{guideIntro.title}</h2>
                <IntroLead>{guideIntro.lead}</IntroLead>
                {guideIntro.paragraphs.map(paragraph => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                <OverviewHighlights>
                  {guideIntro.highlights.map(highlight => (
                    <OverviewHighlight key={highlight.title}>
                      <strong>{highlight.title}</strong>
                      <span>{highlight.text}</span>
                    </OverviewHighlight>
                  ))}
                </OverviewHighlights>
              </OverviewCopy>

              <ServiceCta aria-labelledby="planning-guide-service-cta-heading">
                <h3 id="planning-guide-service-cta-heading">
                  {guideIntro.serviceCtaTitle}
                </h3>
                <p>{guideIntro.serviceCtaText}</p>
                <ServiceCtaGrid>
                  {serviceCtaLinks.map(link => (
                    <ServiceCtaLink key={link.href} to={link.href}>
                      {link.label}
                    </ServiceCtaLink>
                  ))}
                </ServiceCtaGrid>
                <ServiceContactRow>
                  <ServiceContactLink href={serviceCtaPhoneHref}>
                    <PhoneIcon aria-hidden="true" focusable="false" />
                    {i18n.PLANNING_PHONE_LABEL}
                  </ServiceContactLink>
                  <ServiceContactLink
                    href={serviceCtaWhatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    $tone="solid"
                  >
                    <WhatsappIcon aria-hidden="true" focusable="false" />
                    {i18n.PLANNING_WHATSAPP_LABEL}
                  </ServiceContactLink>
                </ServiceContactRow>
              </ServiceCta>
            </OverviewLayout>
          </Overview>

          <Groups>
            {groups.map((group, groupIndex) => {
              const groupKey = String(group.service);
              const cardsId = `planning-guide-cards-${groupIndex}`;
              const visibleCardCount = Math.max(
                1,
                Math.min(group.items.length, visibleCarouselCount),
              );
              const maxCarouselIndex = Math.max(
                0,
                group.items.length - visibleCardCount,
              );
              const safeCarouselIndex = Math.min(
                carouselIndexes[groupKey] ?? 0,
                maxCarouselIndex,
              );
              const visibleItems = group.items.slice(
                safeCarouselIndex,
                safeCarouselIndex + visibleCardCount,
              );
              const hasCarouselControls = group.items.length > visibleCardCount;
              const groupRegion = 'region' in group ? group.region : undefined;
              const groupParentLink =
                buildRegionParentLink(groupRegion) ??
                parentLinkByService.get(group.service as SeoService);
              const groupSectionId = getPlanningGuideGroupSectionId(group);
              const isFirstVenueGroup =
                groupSectionId === PLANNING_GUIDE_VENUES_SECTION_ID &&
                groupIndex === firstVenueGroupIndex;
              const isScrollTarget =
                Boolean(groupSectionId) &&
                (groupSectionId !== PLANNING_GUIDE_VENUES_SECTION_ID ||
                  isFirstVenueGroup);
              const groupThemeVariant: PlanningThemeVariant =
                group.service === SEO_SERVICE_HENNA ? 'henna' : 'wedding';

              return (
                <Group
                  key={group.service}
                  $mode={mode}
                  $variant={groupThemeVariant}
                  id={isScrollTarget ? groupSectionId : undefined}
                  ref={
                    groupSectionId
                      ? registerPlanningSection(
                          groupKey,
                          groupSectionId,
                          isScrollTarget,
                        )
                      : undefined
                  }
                  data-planning-menu-section={groupSectionId}
                >
                  <GroupHeader>
                    <h2>{group.title}</h2>
                    {groupParentLink ? (
                      <GroupParentLink to={groupParentLink.href}>
                        {groupParentLink.label}
                      </GroupParentLink>
                    ) : null}
                  </GroupHeader>
                  <p>{group.subtitle}</p>
                  <CardsCarousel
                    role="region"
                    aria-label={i18n.PLANNING_CAROUSEL_LABEL(group.title)}
                  >
                    {hasCarouselControls ? (
                      <>
                        <CarouselControl
                          type="button"
                          $side={nextControlSide}
                          onClick={() =>
                            moveCarousel(
                              groupKey,
                              maxCarouselIndex,
                              safeCarouselIndex + 1,
                            )
                          }
                          disabled={safeCarouselIndex >= maxCarouselIndex}
                          aria-controls={cardsId}
                          aria-label={i18n.PLANNING_NEXT_LABEL}
                        >
                          {nextControlSymbol}
                        </CarouselControl>
                        <CarouselControl
                          type="button"
                          $side={previousControlSide}
                          onClick={() =>
                            moveCarousel(
                              groupKey,
                              maxCarouselIndex,
                              safeCarouselIndex - 1,
                            )
                          }
                          disabled={safeCarouselIndex === 0}
                          aria-controls={cardsId}
                          aria-label={i18n.PLANNING_PREVIOUS_LABEL}
                        >
                          {previousControlSymbol}
                        </CarouselControl>
                      </>
                    ) : null}
                    <Cards
                      id={cardsId}
                      $visible={visibleCardCount}
                      $dir={direction}
                      aria-live="polite"
                    >
                      {visibleItems.map(item => {
                        const cityLabel = item.locationCity
                          ? i18n.translateCity(item.locationCity)
                          : undefined;

                        return (
                          <Card key={item.href} to={item.href}>
                            <GuideCardMedia
                              item={item}
                              cityLabel={cityLabel}
                              cityVisualLabel={
                                i18n.PLANNING_CITY_VENUE_GUIDE.visualLabel
                              }
                            />
                            <strong>{item.title}</strong>
                            <span>{item.description}</span>
                          </Card>
                        );
                      })}
                    </Cards>
                  </CardsCarousel>
                </Group>
              );
            })}
          </Groups>
        </Main>

        <Footer
          showPlanningParentLinks
          showFeaturedPlanningGuideLink={false}
          variant="planning"
        />
      </Page>
    </>
  );
}
